import React from 'react';
import { CaseStudyShell } from '../components/case-study/CaseStudyShell';
import { CaseHeader } from '../components/case-study/CaseHeader';
import { CaseSection } from '../components/case-study/CaseSection';
import { Figure } from '../components/case-study/Figure';
import { NextCase } from '../components/case-study/NextCase';
import { Reveal } from '../components/Reveal';
import { KordisChatDemo } from '../components/kordis/KordisChatDemo';
import { PrototypeMock } from '../components/kordis/PrototypeMock';
import { DiscoveryBoard } from '../components/kordis/DiscoveryBoard';
import { TrustPatterns } from '../components/kordis/TrustPatterns';
import { KitSpecimens } from '../components/kordis/KitSpecimens';
import { ImageFigure } from '../components/case-study/ImageFigure';
import { kordisKit } from '../data/kordisKit';
import { kordisInteractionModel, kordisMeta } from '../data/kordis';
export function KordisCaseStudy() {
  return <CaseStudyShell data-id="element-877" data-mp="nx4qd18hmor5">
      <CaseHeader overline="Case study · Financial services AI" title="Kordis: an AI CFO whose numbers you can check" intro="Kordis is a chat-first AI CFO. Fractional finance leaders ask questions in plain English and get answers back with their sources attached. As PM and lead designer, I designed the interaction model that makes those answers checkable: inline citations, confidence in plain words, and clarifying questions when a request is ambiguous." meta={kordisMeta} data-id="element-878" data-mp="1lrftwq2nz692" />

      <Figure className="mt-14" caption="Every figure links back to where it came from. Hover or tap a citation to see its source." data-id="element-879" data-mp="20gvibzeq2zll">
        <KordisChatDemo data-id="element-880" data-mp="2eetx35wygy1m" />
      </Figure>

      <CaseSection index="01" label="The problem" title="Nobody could trace where the numbers came from" intro={<>
            <p data-id="element-882" data-mp="k2m4w0c0qv9j">
              Fractional CFOs juggle about a dozen clients at once, working out of spreadsheets and dashboards built for
              questions someone already thought to ask. A new question can take hours.
            </p>
            <p data-id="element-883" data-mp="2k4bxq9h4odfi">
              Even when an answer arrives, there's no trail back to where the numbers came from and no sign of how fresh
              the data is. Making it faster wouldn't fix that.
            </p>
          </>} data-id="element-881" data-mp="2edr1wlslo9fw" />

      <CaseSection index="02" label="Discovery" title="Finance leaders weren't afraid of AI. They didn't trust it." intro={<>
            <p data-id="element-885" data-mp="332o6hu44nlak">
              I started with questions for the team about how CFOs actually move between clients, then went where
              finance leaders talk candidly: Reddit communities, industry reports and AI tool round-ups.
            </p>
            <p data-id="element-886" data-mp="3cs7q51znzj6h">
              The complaints all pointed the same way. Tools hid their data, and automation broke down at data prep. The
              opportunity wasn't more automation. It was automation a CFO could check.
            </p>
          </>} data-id="element-884" data-mp="2t634zirbgsbh">
        <DiscoveryBoard data-id="element-887" data-mp="1a7euqanhdhm1" />
      </CaseSection>

      <CaseSection index="03" label="First prototype" title="Proving the concept with Kronos+" intro={<p data-id="element-889" data-mp="114vuzzezrl40">
            I turned the core features into a rough prototype: a copilot that spots a problem and lets the CFO model
            its impact live, the "can we afford two engineers?" question clients ask on calls.
          </p>} data-id="element-888" data-mp="1a3zf96pwa6k3">
        <Figure caption="My first prototype, Kronos+: a rough layout sketch, then a what-if scenario flow where the copilot spots a cost spike and models its impact on runway. Deliberately basic, built to prove the concept." data-id="element-890" data-mp="1syxveau18jkw">
          <PrototypeMock data-id="element-891" data-mp="1f5oqamd4ak86" />
        </Figure>
      </CaseSection>

      <CaseSection index="04" label="What I did" title="An interaction model for an agent you can question" data-id="element-892" data-mp="1f56prdjf9jug">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-893" data-mp="2cmnii4r0xvns">
          {kordisInteractionModel.map((item, i) => <Reveal key={item.title} delay={i * 0.05} className="h-full" data-id="element-894" data-mp="1reux7gmn4a1v" data-mpi={i}>
              <div className="h-full rounded-2xl bg-surface p-5" data-id="element-895" data-mp="1rgxywxx5nrmf">
                <h3 className="text-sm font-semibold text-fg" data-id="element-896" data-mp="1wehf43deyeh0">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fg-muted" data-id="element-897" data-mp="1ieom21eezm5v">{item.body}</p>
              </div>
            </Reveal>)}
        </div>
        <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-fg-muted" data-id="element-898" data-mp="1neukq1j3q8xg">
          I also defined the brand and a token-based component set, and I ran QA on the CFO chat.
        </p>
      </CaseSection>

      <CaseSection index="05" label="Designing for trust" title="Every number traces back to its source" intro={<>
            <p data-id="element-900" data-mp="26aauduiiom2h">Every answer shows the figures, where they came from and how current the data is.</p>
            <p data-id="element-901" data-mp="2tzvewhubjrdv">
              Confidence is stated in words, and when Kordis isn't sure, it asks. Finance leaders sign off on these
              numbers, so they need to know when an answer is shaky.
            </p>
          </>} data-id="element-899" data-mp="21cska0srk0sw">
        <TrustPatterns data-id="element-902" data-mp="345mxr8tf6c7q" />
      </CaseSection>

      <CaseSection index="06" label="Component kit" title="A kit built for an agent, not a dashboard" intro={<>
            <p data-id="element-904" data-mp="3cx6szkyum3xy">
              Standard UI kits have no parts for an AI that drafts, guesses and works in the background. So I built the
              Chat CFO UI Kit: nine components in Magic Patterns, shared with engineering as code.
            </p>
            <p data-id="element-905" data-mp="2l8bznrjixotv">
              Each one carries a trust rule. Insights state their confidence and source. Anything the AI writes stays an
              editable draft. And while it works, it says what it's doing.
            </p>
          </>} data-id="element-903" data-mp="344vft0sqwczx">
        <Figure caption="Three components from the kit, live. Try Edit on the journal entry." data-id="element-906" data-mp="ef9xcpn9b1s7">
          <KitSpecimens data-id="element-907" data-mp="9hhwv78jq7ce" />
        </Figure>
        <ImageFigure className="mt-10" {...kordisKit.overview} caption="The full Chat CFO UI Kit in Magic Patterns." data-id="element-908" data-mp="9gq92il6xdwo" />
      </CaseSection>

      <CaseSection index="07" label="Outcome" title="Shipped to engineering, heading to pilot" intro={<p data-id="element-910" data-mp="qg9nibvdak2y">
            The chat interaction model, the citation and trust patterns, and a token-based component set are in the
            hands of the engineering team. Kordis is in active build toward its first pilot.
          </p>} data-id="element-909" data-mp="li7ylab9fqjk" />

      <NextCase title="Vendoo: an analytics dashboard resellers could do taxes with" href="/work/vendoo" data-id="element-911" data-mp="2ttd12jpp4q5" />
    </CaseStudyShell>;
}